import type { ChainId } from '@human-protocol/sdk';

export type EvmAddress = `0x${string}`;

export enum ExchangeType {
  CEX = 'cex',
  DEX = 'dex',
}

export enum CampaignType {
  MARKET_MAKING = 'MARKET_MAKING',
  COMPETITIVE_MARKET_MAKING = 'COMPETITIVE_MARKET_MAKING',
  THRESHOLD_MARKET_MAKING = 'THRESHOLD_MARKET_MAKING',
  HOLDING = 'HOLDING',
  THRESHOLD = 'THRESHOLD',
}

export enum CampaignStatus {
  ACTIVE = 'active',
  TO_CANCEL = 'to_cancel',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

export type TokenData = {
  name: string;
  label?: string;
  icon?: string;
};

export type CampaignsStats = {
  n_active_campaigns: number;
  rewards_pool_usd: string;
};

export type Exchange = {
  enabled: boolean;
  name: string;
  display_name: string;
  url: string;
  logo: string;
  type: ExchangeType;
};

export type ExchangeApiKeyData = {
  exchange_name: string;
  api_key: string;
  extras?: Record<string, string>;
  is_valid: boolean;
  missing_permissions: string[];
};

export type Campaign = {
  address: EvmAddress;
  chain_id: number;
  details: {
    // Market Making
    daily_volume_target?: number;
    // Competitive Market Making
    minimum_volume_required?: number;
    rewards_distribution?: number[];
    // Threshold Market Making
    minimum_volume_target?: number;
    // Holding
    daily_balance_target?: number;
    // Threshold
    minimum_balance_target?: number;
    // Threshold-based
    max_participants?: number;
  };
  end_date: string;
  escrow_status: string;
  exchange_name: string;
  exchange_oracle: string;
  final_results_url: string | null;
  fund_amount: string;
  fund_token: string;
  fund_token_decimals: number;
  fund_token_symbol: string;
  intermediate_results_url: string | null;
  launcher: string;
  recording_oracle: string;
  reputation_oracle: string;
  cancellation_requested_at: number | null;
  start_date: string;
  status: CampaignStatus;
  symbol: string;
  type: CampaignType;
  amount_paid: string;
  balance: string;
};

export type JoinedCampaign = {
  address: EvmAddress;
  chain_id: number;
  cancellation_requested_at: number | null;
  details: {
    // Market Making
    daily_volume_target?: number;
    // Competitive Market Making
    minimum_volume_required?: number;
    // Threshold Market Making
    minimum_volume_target?: number;
    // Holding
    daily_balance_target?: number;
    // Threshold
    minimum_balance_target?: number;
    // Threshold-based
    max_participants?: number;
  };
  end_date: string;
  exchange_name: string;
  fund_amount: number;
  fund_token: string;
  start_date: string;
  status: CampaignStatus;
  symbol: string;
  type: CampaignType;
};

export type CampaignDetails = Campaign & {
  exchange_oracle_fee_percent: number;
  recording_oracle_fee_percent: number;
  reputation_oracle_fee_percent: number;
  reserved_funds: string;
};

export type CampaignDailyPaidAmounts = {
  results: {
    date: string;
    amount: string;
  }[];
};

export type CampaignsResponse = {
  results: Campaign[];
  has_more: boolean;
};

export type JoinedCampaignsResponse = {
  results: JoinedCampaign[];
  has_more: boolean;
};

export type LeaderboardEntryDto = {
  address: EvmAddress;
  result: number;
  score: number;
  estimated_reward: number;
  estimated_reward_gross: number;
};

export type LeaderboardEntry = LeaderboardEntryDto & {
  rank: number;
};

export type LeaderboardResponseDto = {
  data: LeaderboardEntryDto[];
  total: number;
  updated_at: string;
};

export type LeaderboardData = Omit<LeaderboardResponseDto, 'data'> & {
  data: LeaderboardEntry[];
};

type BaseManifestDto = {
  exchange: string;
  start_date: string;
  end_date: string;
};

export type MarketMakingManifestDto = BaseManifestDto & {
  type: CampaignType.MARKET_MAKING;
  pair: string;
  daily_volume_target: number;
};

export type HoldingManifestDto = BaseManifestDto & {
  type: CampaignType.HOLDING;
  symbol: string;
  daily_balance_target: number;
};

export type ThresholdManifestDto = BaseManifestDto & {
  type: CampaignType.THRESHOLD;
  symbol: string;
  minimum_balance_target: number;
};

export type CompetitiveMmManifestDto = BaseManifestDto & {
  type: CampaignType.COMPETITIVE_MARKET_MAKING;
  pair: string;
  minimum_volume_required: number;
  rewards_distribution: number[];
};

export type ThresholdMmManifestDto = BaseManifestDto & {
  type: CampaignType.THRESHOLD_MARKET_MAKING;
  pair: string;
  minimum_volume_target: number;
  max_participants: number;
};

export type ManifestUploadDto =
  | MarketMakingManifestDto
  | HoldingManifestDto
  | ThresholdManifestDto
  | CompetitiveMmManifestDto
  | ThresholdMmManifestDto;

export type CampaignsQueryParams = {
  chain_id: ChainId;
  status?: CampaignStatus;
  launcher?: string;
  type?: CampaignType[];
  exchange?: string[];
  limit?: number;
  skip?: number;
};

export type OracleFees = {
  exchange_oracle_fee: string;
  recording_oracle_fee: string;
  reputation_oracle_fee: string;
};

export type HoldingResult = {
  token_balance: number;
};

export type HoldingMeta = {
  total_balance: number;
};

export type MarketMakingResult = {
  total_volume: number;
};

export type MarketMakingMeta = {
  total_volume: number;
};

export type ThresholdResult = {
  token_balance: number;
};

export type ThresholdMeta = {
  total_balance: number;
  total_score: number;
};

export type MyMeta = HoldingResult | MarketMakingResult | ThresholdResult;
export type TotalMeta = HoldingMeta | MarketMakingMeta | ThresholdMeta;

type BaseCampaignFormValues = {
  exchange: string;
  start_date: Date;
  end_date: Date;
  fund_token: string;
  fund_amount: number | string;
};

export type MarketMakingFormValues = BaseCampaignFormValues & {
  type: CampaignType.MARKET_MAKING;
  pair: string;
  daily_volume_target: number | string;
};

export type HoldingFormValues = BaseCampaignFormValues & {
  type: CampaignType.HOLDING;
  symbol: string;
  daily_balance_target: number | string;
};

export type ThresholdFormValues = BaseCampaignFormValues & {
  type: CampaignType.THRESHOLD;
  symbol: string;
  minimum_balance_target: number | string;
};

export type CompetitiveMmFormValues = BaseCampaignFormValues & {
  type: CampaignType.COMPETITIVE_MARKET_MAKING;
  pair: string;
  minimum_volume_required: number | string;
  rewards_distribution: number[];
};

export type ThresholdMmFormValues = BaseCampaignFormValues & {
  type: CampaignType.THRESHOLD_MARKET_MAKING;
  pair: string;
  minimum_volume_target: number | string;
  max_participants: number | string;
};

export type CampaignFormValues =
  | MarketMakingFormValues
  | HoldingFormValues
  | ThresholdFormValues
  | CompetitiveMmFormValues
  | ThresholdMmFormValues;

export enum CampaignJoinStatus {
  USER_ALREADY_JOINED = 'already_joined',
  USER_CAN_JOIN = 'can_join',
  JOIN_IS_CLOSED = 'join_closed',
  NOT_AVAILABLE = 'not_available',
}

export type CheckCampaignJoinStatusResponse = {
  status: CampaignJoinStatus;
  joined_at?: string;
  reason?: string;
};

export enum AllowanceType {
  UNLIMITED = 'unlimited',
  CUSTOM = 'custom',
}

export enum CampaignsTabFilter {
  ALL = 'all',
  JOINED = 'joined',
  HOSTED = 'hosted',
}

export type UserPreferences = {
  campaigns_autojoin: {
    enabled: boolean;
    exchanges: string[];
    campaign_types: string[];
    tokens: string[];
  };
  telegram_user_id: string | null;
  notifications: {
    campaigns_autojoin: boolean;
  };
};

export type PatchPreferencesDto = Partial<UserPreferences>;

export type UserInfo = {
  id: string;
  evm_address: EvmAddress;
  preferences: UserPreferences;
};
